import React, { useState } from 'react';
import { Calculator, Ruler, Scale, ArrowRight, Maximize2 } from 'lucide-react';
import { UnitCategory } from '../types/units';
import { convertValue } from '../utils/conversions';
import { unitsByCategory } from '../utils/units';

const descriptions = {
  title: "Advanced Tools",
  description: "Specialized tools for complex unit calculations and analysis.",
  ratio: {
    title: "Ratio Calculator",
    description: "Calculate and simplify ratios between different units. Perfect for scaling recipes, mixing solutions, or analyzing proportions."
  },
  scale: {
    title: "Scale Calculator",
    description: "Generate scaled values using a multiplication factor. Useful for creating measurement series or analyzing geometric progressions."
  },
  distribution: {
    title: "Distribution Calculator",
    description: "Divide a total value into equal parts. Great for portioning, resource allocation, or creating even distributions."
  }
};

interface AdvancedToolsProps {
  category: UnitCategory;
}

export const AdvancedTools: React.FC<AdvancedToolsProps> = ({ category }) => {
  const [ratioValue1, setRatioValue1] = useState('');
  const [ratioValue2, setRatioValue2] = useState('');
  const [ratioUnit, setRatioUnit] = useState('');
  const [ratioResult, setRatioResult] = useState<string | null>(null);

  const [scaleValue, setScaleValue] = useState('');
  const [scaleFactor, setScaleFactor] = useState('');
  const [scaleUnit, setScaleUnit] = useState('');
  const [scaleResults, setScaleResults] = useState<string[]>([]);

  const [distributionValue, setDistributionValue] = useState('');
  const [distributionParts, setDistributionParts] = useState('');
  const [distributionUnit, setDistributionUnit] = useState('');
  const [distributionResults, setDistributionResults] = useState<string[]>([]);

  const calculateRatio = (e: React.FormEvent) => {
    e.preventDefault();
    if (!ratioValue1 || !ratioValue2 || !ratioUnit) return;

    const ratio = Number(ratioValue1) / Number(ratioValue2);
    setRatioResult(`${ratio.toFixed(4)}:1`);
  };

  const calculateScale = (e: React.FormEvent) => {
    e.preventDefault();
    if (!scaleValue || !scaleFactor || !scaleUnit) return;

    const baseValue = Number(scaleValue);
    const factor = Number(scaleFactor);
    const results = [];

    // Calculate scaled values
    for (let i = 1; i <= 5; i++) {
      const scaledValue = baseValue * (factor ** i);
      results.push(`${scaledValue.toFixed(2)} ${scaleUnit}`);
    }

    setScaleResults(results);
  };

  const calculateDistribution = (e: React.FormEvent) => {
    e.preventDefault();
    if (!distributionValue || !distributionParts || !distributionUnit) return;

    const total = Number(distributionValue);
    const parts = Number(distributionParts);
    const partValue = total / parts;
    
    const results = Array(parts).fill(0).map((_, i) => 
      `Part ${i + 1}: ${partValue.toFixed(2)} ${distributionUnit}`
    );

    setDistributionResults(results);
  };

  return (
    <div className="mt-8 space-y-8">
      <div className="flex items-center gap-2">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Maximize2 className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-800">{descriptions.title}</h2>
          </div>
          <p className="text-gray-600 text-sm">{descriptions.description}</p>
        </div>
      </div>

      {/* Ratio Calculator */}
      <div className="p-6 bg-white/80 backdrop-blur rounded-xl border border-gray-100 shadow-lg">
        <div className="flex items-center gap-2 mb-4">
          <Scale className="w-5 h-5 text-blue-600" />
          <div>
            <h2 className="text-lg font-semibold text-gray-800">{descriptions.ratio.title}</h2>
            <p className="text-gray-600 text-sm mt-1">{descriptions.ratio.description}</p>
          </div>
        </div>
        
        <form onSubmit={calculateRatio} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 items-center">
            <input
              type="number"
              value={ratioValue1}
              onChange={(e) => setRatioValue1(e.target.value)}
              placeholder="First value"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            />
            <input
              type="number"
              value={ratioValue2}
              onChange={(e) => setRatioValue2(e.target.value)}
              placeholder="Second value"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            />
            <select
              value={ratioUnit}
              onChange={(e) => setRatioUnit(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            >
              <option value="">Unit</option>
              {unitsByCategory[category].map(unit => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Calculate Ratio
          </button>
        </form>

        {ratioResult && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <p className="font-medium text-blue-900">Ratio: {ratioResult}</p>
          </div>
        )}
      </div>

      {/* Scale Calculator */}
      <div className="p-6 bg-white/80 backdrop-blur rounded-xl border border-gray-100 shadow-lg">
        <div className="flex items-center gap-2 mb-4">
          <Ruler className="w-5 h-5 text-blue-600" />
          <div>
            <h2 className="text-lg font-semibold text-gray-800">{descriptions.scale.title}</h2>
            <p className="text-gray-600 text-sm mt-1">{descriptions.scale.description}</p>
          </div>
        </div>
        
        <form onSubmit={calculateScale} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
            <input
              type="number"
              value={scaleValue}
              onChange={(e) => setScaleValue(e.target.value)}
              placeholder="Base value"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            />
            <input
              type="number"
              value={scaleFactor}
              onChange={(e) => setScaleFactor(e.target.value)}
              placeholder="Scale factor"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            />
            <select
              value={scaleUnit}
              onChange={(e) => setScaleUnit(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            >
              <option value="">Unit</option>
              {unitsByCategory[category].map(unit => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Calculate Scale
          </button>
        </form>

        {scaleResults.length > 0 && (
          <div className="mt-4 grid grid-cols-2 gap-2">
            {scaleResults.map((result, index) => (
              <div key={index} className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-900">
                  Scale {index + 1}×: {result}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Distribution Calculator */}
      <div className="p-6 bg-white/80 backdrop-blur rounded-xl border border-gray-100 shadow-lg">
        <div className="flex items-center gap-2 mb-4">
          <Calculator className="w-5 h-5 text-blue-600" />
          <div>
            <h2 className="text-lg font-semibold text-gray-800">{descriptions.distribution.title}</h2>
            <p className="text-gray-600 text-sm mt-1">{descriptions.distribution.description}</p>
          </div>
        </div>
        
        <form onSubmit={calculateDistribution} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4">
            <input
              type="number"
              value={distributionValue}
              onChange={(e) => setDistributionValue(e.target.value)}
              placeholder="Total value"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            />
            <input
              type="number"
              value={distributionParts}
              onChange={(e) => setDistributionParts(e.target.value)}
              placeholder="Number of parts"
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            />
            <select
              value={distributionUnit}
              onChange={(e) => setDistributionUnit(e.target.value)}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg"
            >
              <option value="">Unit</option>
              {unitsByCategory[category].map(unit => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Calculate Distribution
          </button>
        </form>

        {distributionResults.length > 0 && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {distributionResults.map((result, index) => (
              <div key={index} className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-900">{result}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};